import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { principalAPI, teacherAPI, parentAPI } from '../services/api';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Pick the right API based on role
  const getAPI = useCallback(() => {
    if (!user) return null;
    if (user.role === 'teacher') return teacherAPI;
    if (user.role === 'principal' || user.role === 'school_admin') return principalAPI;
    if (user.role === 'parent') return parentAPI;
    return null;
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    const notifAPI = getAPI();
    if (!notifAPI) return;
    try {
      const res = await notifAPI.getNotifications();
      const list = res.data.notifications || [];
      setNotifications(list);
      setUnreadCount(list.filter(n => !n.is_read).length);
    } catch (err) {
      // Silently fail — notifications are non-critical
    }
  }, [getAPI]);

  // Initial fetch + poll every 30 seconds
  useEffect(() => {
    const notifAPI = getAPI();
    if (!notifAPI) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [getAPI, fetchNotifications]);

  const markRead = useCallback(async (id) => {
    const notifAPI = getAPI();
    if (!notifAPI) return;
    try {
      await notifAPI.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark notification read error:', err);
    }
  }, [getAPI]);

  const markAllRead = useCallback(async () => {
    const notifAPI = getAPI();
    if (!notifAPI) return;
    try {
      await notifAPI.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all notifications read error:', err);
    }
  }, [getAPI]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, fetchNotifications, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
};
